import {
  getExistingDynamicLayer,
  getExistingDynamicObject,
  normalizeDynamicLayerMessage,
  normalizeDynamicObject,
  checkNegativeSpatialObject,
  filterNegativeSpatialObjects,
  filterDynamicLayers,
} from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { getCallQueueMaxAge } from '@rsos/utils/metaTags';
import {
  ADD_DYNAMIC_LAYER,
  CLEAR_CHECKED_LAYERS,
  CLEAR_RAD_SESSION,
  DEACTIVATE_SPATIAL_LAYER,
  FETCH_CRG_DEEPLINK_START,
  FETCH_CRG_DEEPLINK_SUCCESS,
  FETCH_CRG_DEEPLINK_FAILED,
  FETCH_RAD_SESSION_START,
  FETCH_RAD_SESSION_SUCCESS,
  FETCH_RAD_SESSION_FAILED,
  FORCE_STATIC_LAYER_ON,
  GET_SPATIAL_LAYERS_START,
  GET_SPATIAL_LAYERS_SUCCESS,
  GET_SPATIAL_LAYERS_FAILED,
  GET_SPATIAL_OBJECTS_SUMMARY_SUCCESS,
  GET_SPATIAL_OBJECTS_SUMMARY_FAILURE,
  GET_SPATIAL_OBJECT_DETAILS_SUCCESS,
  GET_LATEST_ACTIVE_SPATIAL_LAYERS,
  SET_ACTIVE_CRG_OBJECT,
  SET_ACTIVE_CRG_OBJECT_FLOOR,
  SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS,
  SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS,
  TOGGLE_SPATIAL_LAYER,
  TOGGLE_SPATIAL_LAYER_GROUP,
  UPDATE_DYNAMIC_LAYER,
  UPDATE_DISABLED_DYNAMIC_LAYERS,
  INACTIVATE_AVIVE_REQUEST,
  FETCH_SPATIAL_OBJECT_DETAILS_START,
  FETCH_SPATIAL_OBJECT_DETAILS_FAILED,
} from '../actions/actionTypes';
import { normalizePhone } from '../components/JurisdictionView/AviveAED/aviveSlice';

const ACTIVE_STATUS = 'active';
const INACTIVE_STATUS = 'inactive';

// TODO: check if needed
// radSession is here for now only because it only handles 911inform payload
// for 3.4.0, should address this once the spatial layers and session api
// are updated
const initialState = {
  activeCrgObject: {
    spatialObject: {},
    activeFloor: null,
    deeplink: {
      loading: false,
      error: null,
      data: {},
    },
  },
  allSpatialLayers: [], // this is for both JV and QV
  checkSpatialLayerStatus: false,
  dynamicLayers: [],
  fetchingSpatialLayers: false,
  fetchingRADSession: false,
  fetchingRADSessionError: null,
  error: null,
  latestActiveSpatialLayers: {}, // Note: new JV -> Map component doesn't use this for updating the spatial layers
  radSession: {}, // this is for QV only
  staticLayers: [],
  disabledDynamicLayers: [],
  popover: null,
  // Spatial objects optimization cache
  objectDetailsCache: {},
};

const spatialLayers = (state = initialState, action) => {
  switch (action.type) {
    case INACTIVATE_AVIVE_REQUEST: {
      const aviveRequestLayer = state.dynamicLayers.find(
        layer => layer.key === 'avive_aed_request',
      );
      if (aviveRequestLayer) {
        // find the spatial object with the phone number and add the inactive: false
        // flag to it, then save it to the reducer's state.
        const updatedSpatialObjects = aviveRequestLayer.spatial_objects.map(
          spatialObject => {
            if (
              normalizePhone(spatialObject.attributes.phone_number) ===
              normalizePhone(action.phone)
            ) {
              return {
                ...spatialObject,
                attributes: {
                  ...spatialObject.attributes,
                  inactiveRequest: true,
                },
              };
            }
            return spatialObject;
          },
        );

        const updatedDynamicLayersList = state.dynamicLayers.map(
          dynamicLayer => {
            if (dynamicLayer.key === 'avive_aed_request') {
              return {
                ...dynamicLayer,
                spatial_objects: updatedSpatialObjects,
              };
            }
            return dynamicLayer;
          },
        );

        return {
          ...state,
          dynamicLayers: updatedDynamicLayersList,
        };
      }

      return state;
    }

    case ADD_DYNAMIC_LAYER: {
      const filteredDynamicLayers = filterDynamicLayers(state.dynamicLayers);

      // If an incoming dynamic spatial object's `key` already exists, add
      // the object to the spatial layer's spatial_objects
      const existingDynamicLayer = getExistingDynamicLayer(
        filteredDynamicLayers,
        action.layer,
      );
      const incomingSpatialObject = action.layer;
      if (existingDynamicLayer) {
        const updatedDynamicLayersList = filteredDynamicLayers.map(
          dynamicLayer => {
            if (dynamicLayer.key === incomingSpatialObject.layer.key) {
              const normalizedSpatialObject = {
                ...normalizeDynamicObject(incomingSpatialObject),
              };
              const updatedSpatialObjects = [
                ...dynamicLayer.spatial_objects,
                normalizedSpatialObject,
              ];
              return {
                ...dynamicLayer,
                spatial_objects: updatedSpatialObjects,
              };
            }
            return dynamicLayer;
          },
        );
        return {
          ...state,
          dynamicLayers: updatedDynamicLayersList,
        };
      }
      return {
        ...state,
        dynamicLayers: [
          ...filteredDynamicLayers,
          filterNegativeSpatialObjects(
            normalizeDynamicLayerMessage(action.layer),
          ),
        ],
      };
    }
    case UPDATE_DYNAMIC_LAYER: {
      const updatedObject = action.layer;
      const existingObject = getExistingDynamicObject(
        state.dynamicLayers,
        updatedObject,
      );

      // Negative spatial objects are those that have their display flag
      // set to 'False'. This signals to the UI that the marker should be
      // removed from the map and that allCoordinates should be reset.
      const isNegativeSpatialObject = checkNegativeSpatialObject(updatedObject);

      const updatedDynamicLayersList = state.dynamicLayers.map(dynamicLayer => {
        if (
          dynamicLayer &&
          existingObject &&
          dynamicLayer.key === existingObject.key
        ) {
          const updatedSpatialObjects = dynamicLayer.spatial_objects.map(
            object => {
              // allCoordinates is used to render the tailing path of
              // the marker on updated coordinates.
              // Optimized: If same consecutive coordinates arrive,
              // don't duplicate the coordinate. This allows for smarter
              // rendering since the list of all coordinates will only
              // update when new lat/long positions are recorded. This
              // avoids unnecessary re-renders in the useDynamicLayers hook.
              let allCoordinates = object.allCoordinates || [];
              const newCoordinate = {
                lat: updatedObject.shape.features[0].geometry.coordinates[1],
                lng: updatedObject.shape.features[0].geometry.coordinates[0],
              };
              let isSameCoordinate = false;
              let mostRecentCoordinate = null;
              if (allCoordinates.length >= 1) {
                mostRecentCoordinate = allCoordinates[0];
                isSameCoordinate =
                  newCoordinate.lat === mostRecentCoordinate.lat &&
                  newCoordinate.lng === mostRecentCoordinate.lng;
              }
              allCoordinates = [
                isSameCoordinate ? null : newCoordinate,
                ...allCoordinates,
              ].filter(item => item !== null);

              if (object.external_id === updatedObject.external_id) {
                return {
                  ...object,
                  allCoordinates: isNegativeSpatialObject ? [] : allCoordinates,
                  attributes: {
                    ...updatedObject.attributes,
                  },
                  rg_address: updatedObject.rg_address,
                  date_created: updatedObject.date_created,
                };
              }
              return object;
            },
          );

          return {
            ...dynamicLayer,
            spatial_objects: updatedSpatialObjects,
          };
        }
        return dynamicLayer;
      });
      return {
        ...state,
        dynamicLayers: updatedDynamicLayersList,
      };
    }
    case GET_SPATIAL_LAYERS_START: {
      return {
        ...state,
        fetchingSpatialLayers: true,
      };
    }
    case GET_SPATIAL_LAYERS_SUCCESS: {
      const { staticLayers, dynamicLayers } = action;

      return {
        ...state,
        dynamicLayers,
        fetchingSpatialLayers: false,
        staticLayers,
      };
    }
    case GET_SPATIAL_LAYERS_FAILED: {
      return {
        ...state,
        fetchingSpatialLayers: false,
        error: action.error,
      };
    }
    case GET_LATEST_ACTIVE_SPATIAL_LAYERS: {
      // TODO v2: confirmed is ok to share the getCallQueueMaxAge for 3.4 because
      // both of them are set to 30 mins. Update from hydra to have its own
      // value instead
      const slMaxAge = getCallQueueMaxAge() * 1000;
      const nowTS = Math.round(new Date().getTime());
      const currentStaticLayersList = [...state.staticLayers];

      // If a spatial layer's spatial objects have a matching ID, update the
      // spatial object's status
      const updatedStaticLayerList = currentStaticLayersList.map(
        spatialLayer => {
          const updatedSpatialObjects = spatialLayer.spatial_objects.map(
            spatialObject => {
              if (spatialObject.id === action.message.body.spatial_object_id) {
                return {
                  ...spatialObject,
                  deepLink: action.message.body.deep_link_url
                    ? action.message.body.deep_link_url
                    : null,
                  expiredTime: nowTS + slMaxAge,
                  status: ACTIVE_STATUS,
                };
              }
              return spatialObject;
            },
          );
          return {
            ...spatialLayer,
            spatial_objects: updatedSpatialObjects,
          };
        },
      );
      const newState = {
        ...state,
        latestActiveSpatialLayers: {
          spatial_object_id: action.message.body.spatial_object_id,
          deep_link_url: action.message.body.deep_link_url
            ? action.message.body.deep_link_url
            : null,
          status: ACTIVE_STATUS,
        },
        checkSpatialLayerStatus: true,
        staticLayers: updatedStaticLayerList,
      };
      return newState;
    }
    case SET_STATIC_SPATIAL_OBJECT_ACTIVE_STATUS: {
      // TODO (vkoo): Can combine with GET_LATEST_ACTIVE_SPATIAL_LAYERS. it was
      // legacy for 911inform only. this one is more generic.
      // `latestActiveSpatialLayers` is unused
      // Sets the spatial object's status for toggling the color of its polygon
      const updatedStaticLayersList = [...state.staticLayers].map(layer => {
        const updatedSpatialObjects = layer?.spatial_objects.map(
          spatialObject => {
            if (spatialObject.id === action.payload?.data?.spatial_object_id) {
              return {
                ...spatialObject,
                status: ACTIVE_STATUS,
                intersectingCalls: Array.from(
                  new Set([
                    ...(spatialObject?.intersectingCalls
                      ? spatialObject.intersectingCalls
                      : []),
                    action.payload?.data?.caller_id,
                  ]),
                ).filter(c => !!c),
              };
            }
            return {
              ...spatialObject,
            };
          },
        );
        return {
          ...layer,
          spatial_objects: updatedSpatialObjects,
        };
      });

      return { ...state, staticLayers: updatedStaticLayersList };
    }
    case SET_STATIC_SPATIAL_OBJECT_INACTIVE_STATUS: {
      // TODO (vkoo): Can combine with DEACTIVATE_SPATIAL_LAYER
      // `latestActiveSpatialLayers` is unused
      const updatedStaticLayersList = [...state.staticLayers].map(layer => {
        const updatedSpatialObjects = layer?.spatial_objects.map(
          spatialObject => {
            if (spatialObject.id === action.payload?.data?.spatial_object_id) {
              return {
                ...spatialObject,
                status: INACTIVE_STATUS,
                intersectingCalls: Array.from(
                  new Set([
                    ...(spatialObject?.intersectingCalls
                      ? spatialObject.intersectingCalls
                      : []),
                  ]),
                ).filter(c => c !== action.payload?.data?.caller_id),
              };
            }
            return {
              ...spatialObject,
            };
          },
        );
        return {
          ...layer,
          spatial_objects: updatedSpatialObjects,
        };
      });
      return { ...state, staticLayers: updatedStaticLayersList };
    }
    case DEACTIVATE_SPATIAL_LAYER: {
      // TODO: update for dynamic layers usage
      let updatedStaticLayerList = [...state.staticLayers];
      let updatedLatestActiveSpatialLayer = {
        ...state.latestActiveSpatialLayers,
      };
      updatedStaticLayerList.forEach(spatialLayer => {
        if (spatialLayer.id === action.inActiveID) {
          spatialLayer.status = INACTIVE_STATUS;
        }
      });

      if (
        updatedLatestActiveSpatialLayer.spatial_object_id === action.inActiveID
      ) {
        updatedLatestActiveSpatialLayer.status = INACTIVE_STATUS;
      }
      return {
        ...state,
        checkSpatialLayerStatus: false,
        latestActiveSpatialLayers: updatedLatestActiveSpatialLayer,
        staticLayers: updatedStaticLayerList,
      };
    }
    case FORCE_STATIC_LAYER_ON: {
      const layerSource = action.layerSource;
      const newStaticLayers = state.staticLayers.map(layer => {
        if (layer.source === layerSource) {
          return {
            ...layer,
            isChecked: true,
          };
        }
        return layer;
      });
      return {
        ...state,
        staticLayers: newStaticLayers,
      };
    }
    case TOGGLE_SPATIAL_LAYER: {
      const { layerKey, isDynamic } = action;

      if (isDynamic) {
        const newDynamicLayers = state.dynamicLayers.map(layer => {
          if (layer.key === layerKey) {
            const isChecked = !layer.isChecked;

            return {
              ...layer,
              isChecked,
            };
          } else {
            return { ...layer };
          }
        });
        return {
          ...state,
          dynamicLayers: newDynamicLayers,
        };
      } else {
        const newStaticLayers = state.staticLayers.map(layer => {
          if (layer.key === layerKey) {
            const isChecked = !layer.isChecked;

            return {
              ...layer,
              isChecked,
            };
          } else {
            return { ...layer };
          }
        });
        return {
          ...state,
          staticLayers: newStaticLayers,
        };
      }
    }
    case TOGGLE_SPATIAL_LAYER_GROUP: {
      const { layersKeys } = action;

      const newStaticLayers = state.staticLayers.map(layer => {
        if (layersKeys.includes(layer.key)) {
          const isChecked = !layer.isChecked;

          return {
            ...layer,
            isChecked,
          };
        } else {
          return { ...layer };
        }
      });
      return {
        ...state,
        staticLayers: newStaticLayers,
      };
    }
    case UPDATE_DISABLED_DYNAMIC_LAYERS: {
      const { layerKey, isDisabled } = action;
      if (isDisabled) {
        if (state?.disabledDynamicLayers?.includes(layerKey)) {
          return state;
        }
        const newDisabledDynamicLayers = [
          ...state.disabledDynamicLayers,
          layerKey,
        ];
        return {
          ...state,
          disabledDynamicLayers: newDisabledDynamicLayers,
        };
      } else {
        const newDisabledDynamicLayers = state.disabledDynamicLayers.filter(
          layer => layer !== layerKey,
        );
        return {
          ...state,
          disabledDynamicLayers: newDisabledDynamicLayers,
        };
      }
    }
    case CLEAR_CHECKED_LAYERS: {
      const newDynamicLayers = state.dynamicLayers.map(layer => ({
        ...layer,
        isChecked: false,
      }));
      const newStaticLayers = state.staticLayers.map(layer => ({
        ...layer,
        isChecked: false,
      }));
      return {
        ...state,
        dynamicLayers: newDynamicLayers,
        staticLayers: newStaticLayers,
      };
    }
    case SET_ACTIVE_CRG_OBJECT: {
      return {
        ...state,
        activeCrgObject: action.payload,
      };
    }
    case SET_ACTIVE_CRG_OBJECT_FLOOR: {
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          activeFloor: {
            ...action.floor,
          },
        },
      };
    }
    case FETCH_CRG_DEEPLINK_START: {
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          deeplink: {
            ...state.activeCrgObject.deeplink,
            loading: true,
            error: null,
          },
        },
      };
    }
    case FETCH_CRG_DEEPLINK_SUCCESS: {
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          deeplink: {
            ...state.activeCrgObject.deeplink,
            loading: false,
            data: action.data,
            error: null,
          },
        },
      };
    }
    case FETCH_CRG_DEEPLINK_FAILED: {
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          deeplink: {
            ...state.activeCrgObject.deeplink,
            loading: false,
            error: action.error,
          },
        },
      };
    }
    // TODO confirm if still need RAD session handling
    case FETCH_RAD_SESSION_START: {
      return {
        ...state,
        fetchingRADSession: true,
      };
    }
    case FETCH_RAD_SESSION_SUCCESS: {
      let newState = { ...state };
      if (action.payload['911_inform']) {
        // this whole block is same as case GET_LATEST_ACTIVE_SPATIAL_LAYERS
        // but wanted to separate it since it is from session API,
        // also the handling of 'deactive' the spatial layer is different than JV
        const spatialLayerData = action.payload['911_inform']['data'];
        let currentLayers = [...state.staticLayers];
        const updatedStaticLayersList = currentLayers.map(spatialLayer => {
          if (
            spatialLayer.id ===
            spatialLayerData['active_building'].spatial_object_id
          ) {
            return {
              ...spatialLayer,
              deepLink: spatialLayerData['active_building'].deep_link_url,
              status: 'active',
            };
          }

          return spatialLayer;
        });
        newState = {
          ...newState,
          checkSpatialLayerStatus: true,
          fetchingRADSession: false,
          radSession: {
            ...state.radSession,
            spatial_object_id:
              spatialLayerData['active_building'].spatial_object_id,
            deep_link_url: spatialLayerData['active_building'].deep_link_url,
            status: 'active',
          },
          staticLayers: updatedStaticLayersList,
        };
      }

      return newState;
    }
    case FETCH_RAD_SESSION_FAILED: {
      return {
        ...state,
        fetchingRADSession: false,
        fetchingRADSessionError: action.error,
      };
    }
    case CLEAR_RAD_SESSION: {
      return {
        ...state,
        radSession: {},
      };
    }
    case 'APP_INIT_START': {
      return state;
    }
    case GET_SPATIAL_OBJECTS_SUMMARY_SUCCESS: {
      const { staticLayers, dynamicLayers } = action.payload;
      return {
        ...state,
        staticLayers,
        dynamicLayers,
        objectDetailsCache: {},
        error: null,
      };
    }
    case GET_SPATIAL_OBJECTS_SUMMARY_FAILURE:
      return {
        ...state,
        error: action.payload.error,
      };
    case FETCH_SPATIAL_OBJECT_DETAILS_START: {
      // We reuse deeplink loading state as the primary indicator for the modal
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          deeplink: {
            ...state.activeCrgObject.deeplink,
            loading: true,
            error: null,
          },
        },
      };
    }
    case GET_SPATIAL_OBJECT_DETAILS_SUCCESS: {
      const { objectDetailsCache, activeCrgObject } = action.payload;
      return {
        ...state,
        objectDetailsCache,
        activeCrgObject,
      };
    }
    case FETCH_SPATIAL_OBJECT_DETAILS_FAILED: {
      return {
        ...state,
        activeCrgObject: {
          ...state.activeCrgObject,
          deeplink: {
            ...state.activeCrgObject.deeplink,
            loading: false,
            error: action.error,
          },
        },
      };
    }
    default:
      return state;
  }
};

export default spatialLayers;
